import React, { FC, useRef } from 'react';
import { Field } from 'react-final-form';
import { FieldArrayRenderProps as WrappedFieldArrayProps } from 'react-final-form-arrays';
import { QuickQuoteFormProps } from './QuickQuoteTypes';
import { QUOTE_FORM_CONSTANTS, MAX_TRAVELLERS } from '@trip-ux/constants';
import { GenericInput, GenericInputCurrency, GenericInputError } from '../../shared/components';

const handleAgeChange = e => {
  if (!/^\d*$/.test(e.currentTarget.value)) {
    e.preventDefault();
  }
};

const handleCostChange = e => {
  if (!/^((\d+)([,\.])?(\d{1,2})?)?$/.test(e.currentTarget.value)) {
    e.preventDefault();
  }
};

export const Travellers: FC<WrappedFieldArrayProps<QuickQuoteFormProps, HTMLInputElement> & QuickQuoteFormProps> = ({
  fields,
  meta,
  labels,
  config
}) => {
  const ageInputRefs = useRef(new Array(fields.length));
  return (
    <div className="travellers my-3">
      <div
        role="list"
        aria-label={labels.formLabels.traveller.travellersAria}
        className="d-flex flex-wrap justify-content-between">
        {fields.map((field, index) => (
          <div key={`traveller-${index}`} role="listitem" className="row-ele traveller">
            <div className="traveller-label" id={`traveller-label-${index}`}>
              {index === 0 ? (
                <div className="d-flex primary-traveller">
                  <span aria-hidden="true" className="primary-icon" />
                  <small>{labels.formLabels.traveller.primary}</small>
                </div>
              ) : (
                <small>{`${labels.formLabels.traveller.travellerText} ${Number(index) + 1}`}</small>
              )}
            </div>
            <div className="traveller-fields">
              <div className="d-flex traveller-inputs">
                <div className="flex-column traveller-input-age">
                  <label id={`traveller-age-label-${index}`}>{labels.formLabels.traveller.age}</label>
                  <Field
                    name={`${field}.age`}
                    component={GenericInput}
                    type="text"
                    id={`traveller-age-input-${index}`}
                    data-testid={`traveller-age-input-${index}`}
                    className="traveller-input"
                    aria-labelledby={`traveller-label-${index} traveller-age-label-${index}`}
                    inputOnChange={handleAgeChange}
                    forwardRef={true}
                    ref={ref => {
                      if (ref) {
                        ageInputRefs.current[index] = ref;
                      }
                    }}
                  />
                </div>
                <div className="flex-column traveller-input-cost">
                  <label id={`traveller-tripCost-label-${index}`}>{labels.formLabels.traveller.tripCost}</label>
                  <Field
                    name={`${field}.tripCost`}
                    component={GenericInputCurrency}
                    type="text"
                    id={`traveller-tripCost-input-${index}`}
                    data-testid={`traveller-tripCost-input-${index}`}
                    className="traveller-input"
                    config={config}
                    aria-labelledby={`traveller-label-${index} traveller-tripCost-label-${index}`}
                  />
                </div>
              </div>
              <div className="d-flex traveller-input-errors">
                <div className="traveller-input-error">
                  <Field
                    name={`${field}.age`}
                    component={GenericInputError}
                    aria-labelledby={`traveller-label-${index} traveller-age-error-${index}`}
                  />
                </div>
                <div className="traveller-input-error">
                  <Field
                    name={`${field}.tripCost`}
                    component={GenericInputError}
                    aria-labelledby={`traveller-label-${index} traveller-tripCost-error-${index}`}
                  />
                </div>
              </div>
              {index > 0 && (
                <button
                  id={`remove-traveller-${index}`}
                  data-testid={`remove-traveller-${index}`}
                  aria-labelledby={`traveller-remove-label-${index} traveller-label-${index}`}
                  onClick={e => {
                    e.preventDefault();
                    fields.remove(index);
                    ageInputRefs.current.splice(index, 1);
                    setTimeout(() => {
                      const nextFocusRef =
                        ageInputRefs.current[index === ageInputRefs.current.length ? index - 1 : index];
                      nextFocusRef.focus();
                    }, 0);
                  }}>
                  <span aria-hidden="true" className="remove-traveller-icon" />
                  <small id={`traveller-remove-label-${index}`}>{labels.formLabels.removeTraveller}</small>
                </button>
              )}
            </div>
          </div>
        ))}
        <div className="row-ele d-flex add-traveller">
          {fields.length < MAX_TRAVELLERS && (
            <button
              id="add-traveller-button"
              data-testid="add-traveller-button"
              onClick={e => {
                e.preventDefault();
                fields.push({});
                setTimeout(() => {
                  const nextFocusRef = ageInputRefs.current[ageInputRefs.current.length - 1];
                  nextFocusRef.focus();
                }, 0);
              }}>
              <span aria-hidden="true" className="add-traveller-icon" />
              <small>{labels.formLabels.addTraveller}</small>
            </button>
          )}
        </div>
        {fields.length % QUOTE_FORM_CONSTANTS.THREE === QUOTE_FORM_CONSTANTS.ONE && (
          <div aria-hidden="true" className="row-ele d-flex" />
        )}
      </div>
    </div>
  );
};
Travellers.displayName = 'Travellers';
